import { apiHelpers, extractApiData } from '@/api/core';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type {
  Device,
  RegisterDeviceRequest,
  RegisterDeviceResponse,
  CheckOwnershipResponse,
  MappedDeviceInfo,
  UpdateDeviceRequest,
  SaveDeviceDataRequest,
  ReassignDeviceRequest,
  ReassignDeviceResponse,
  SavePacketRequest,
} from '@packages/types/device/dto';
import { siteKeys } from '@/api/site/useSite';
import { companyManagementKeys } from '@/api/admin/useCompanyManagement';

// Re-export for backward compatibility
export type {
  
  
  
  MappedDeviceInfo,
  
  
  
  
  
};

export { mapRawDeviceJsonToMappedInfo as mapDeviceJson } from '@packages/utils/device/mappedDeviceInfoFromJson';

export async function checkDeviceOwnership(serialNumber: string): Promise<CheckOwnershipResponse> {
  const response = await apiHelpers.get<CheckOwnershipResponse>(
    'device.checkOwnership',
    { serialNumber },
  );
  return extractApiData(response);
}

// Query Keys
const deviceKeys = {
  all: ['devices'] as const,
  lists: () => [...deviceKeys.all, 'list'] as const,
  listBySite: (siteId: number) => [...deviceKeys.lists(), siteId] as const,
};

// 기기 등록 mutation
export const useRegisterDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: RegisterDeviceRequest): Promise<RegisterDeviceResponse> => {
      const response = await apiHelpers.post<RegisterDeviceResponse>('device.register', data);
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
};

// 기기 목록 조회 query
export const useGetSiteDevices = (
  siteId: number | null,
  options?: { enabled?: boolean }
) => {
  const isEnabled =
    options?.enabled !== undefined ? options.enabled : siteId !== null && siteId > 0;

  return useQuery<Device[]>({
    queryKey: deviceKeys.listBySite(siteId || 0),
    queryFn: async (): Promise<Device[]> => {
      // enabled가 false면 이 함수는 실행되지 않지만, 안전을 위해 체크
      if (!isEnabled) {
        return [];
      }
      // siteId가 null이면 쿼리 파라미터 없이 요청 (SiteManager용)
      const queryParams = siteId ? { site_id: siteId } : undefined;
      const response = await apiHelpers.get<Device[]>('device.list', {}, queryParams);
      return extractApiData(response, { defaultValue: [] as Device[] });
    },
    enabled: isEnabled,
  });
};

// 기기 업데이트 mutation
export const useUpdateDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, data }: { id: number; data: UpdateDeviceRequest }) => {
      const response = await apiHelpers.put(`device.update`, data, { id });
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
};

// 기기 삭제 mutation
export const useDeleteDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      const response = await apiHelpers.delete(`device.delete`, { id });
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
};

// DeviceInfo/Configuration 저장 mutation (기기 등록 없이도 저장 가능)
export const useSaveDeviceData = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: SaveDeviceDataRequest) => {
      const response = await apiHelpers.post('device.saveData', data);
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
};

// 기기 재할당 mutation (동일 회사 내 사이트 간 이동)
export const useReassignDevice = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: ReassignDeviceRequest): Promise<ReassignDeviceResponse> => {
      const response = await apiHelpers.post<ReassignDeviceResponse>('device.reassign', data);
      return extractApiData(response);
    },
    onSuccess: (data) => {
      // 모든 기기 목록 무효화
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      // 이전 사이트와 새 사이트의 기기 목록 무효화
      if (data.device.old_site_id) {
        queryClient.invalidateQueries({ queryKey: deviceKeys.listBySite(data.device.old_site_id) });
      }
      if (data.device.new_site_id) {
        queryClient.invalidateQueries({ queryKey: deviceKeys.listBySite(data.device.new_site_id) });
      }
      // 사이트 목록 무효화 (device_count가 변경되므로)
      // CompanyPage가 사용하는 쿼리 키들
      if (data.device.company_id) {
        queryClient.invalidateQueries({ queryKey: siteKeys.list(data.device.company_id) });
        queryClient.invalidateQueries({ queryKey: companyManagementKeys.sitesByCompany(data.device.company_id) });
      }
      // 전체 사이트 목록도 무효화
      queryClient.invalidateQueries({ queryKey: companyManagementKeys.sites() });
    },
  });
};

// 패킷 저장 mutation (bellconnect와 동일한 로직 사용)
export const useSavePacket = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: SavePacketRequest) => {
      const response = await apiHelpers.post('device.packet', data);
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
    },
  });
};

// 어드민 강제 연결 해제
export const useForceDisconnect = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (serialNumber: string) => {
      const response = await apiHelpers.post('device.forceDisconnect', undefined, {
        serialNumber,
      });
      return extractApiData(response);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: deviceKeys.lists() });
      queryClient.invalidateQueries({ queryKey: ['monitoring'] });
    },
  });
};
